import { useState, useEffect } from "react";
import api from "@/lib/api";
import { CreditCard, RefreshCw, Send, ExternalLink, Copy, AlertTriangle, Webhook } from "lucide-react";

const STAGES = [
    { key: "request_received", label: "Request received" },
    { key: "technician_review", label: "Technician review needed" },
    { key: "estimate_being_prepared", label: "Estimate being prepared" },
    { key: "estimate_sent", label: "Estimate sent" },
    { key: "awaiting_approval", label: "Awaiting approval" },
    { key: "approved", label: "Approved" },
    { key: "invoice_sent", label: "Invoice sent" },
    { key: "paid", label: "Paid" },
    { key: "scheduled", label: "Scheduled" },
    { key: "completed", label: "Completed" },
    { key: "canceled", label: "Canceled" },
];

// Panels are exported individually and composed into tabs by LeadDetail.

function Section({ title, icon: Icon, children, testid }) {
    return (
        <div className="a1-card a1-card-feature space-y-3" data-testid={testid}>
            <div className="flex items-center gap-2">
                {Icon ? <Icon size={18} className="text-[var(--color-accent)]" /> : null}
                <h3 className="text-base">{title}</h3>
            </div>
            {children}
        </div>
    );
}

// ---------- Workflow stage selector ----------
export function WorkflowPanel({ lead, onChange }) {
    const [stage, setStage] = useState(lead.workflow_stage || "request_received");
    const [saving, setSaving] = useState(false);
    useEffect(() => { setStage(lead.workflow_stage || "request_received"); }, [lead.workflow_stage]);
    const save = async (next) => {
        setSaving(true);
        try {
            await api.patch(`/admin/leads/${lead.id}/workflow-stage`, { workflow_stage: next });
            setStage(next);
            onChange?.();
        } finally { setSaving(false); }
    };
    return (
        <Section title="Workflow stage" testid="lead-workflow-panel">
            <p className="text-xs uppercase tracking-[0.18em] font-bold" style={{ color: "var(--color-accent)" }}>
                Customer-visible stage
            </p>
            <select
                className="a1-select"
                value={stage}
                onChange={(e) => save(e.target.value)}
                disabled={saving}
                data-testid="lead-workflow-stage-select"
            >
                {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <p className="a1-helper">
                Customer dashboard shows the matching label. Online stage transitions do not replace technician review.
            </p>
        </Section>
    );
}

// ---------- Payments / Stripe ----------
export function PaymentsPanel({ lead, onChange }) {
    const [busy, setBusy] = useState(null);
    const [amount, setAmount] = useState(275);
    const [error, setError] = useState("");
    const inv = lead.invoice || {};
    const pay = lead.payment || {};

    const run = async (action, body) => {
        setBusy(action); setError("");
        try {
            await api.post(`/admin/leads/${lead.id}/${action}`, body || {});
            onChange?.();
        } catch (e) {
            setError(e?.response?.data?.detail || "Action failed.");
        } finally { setBusy(null); }
    };

    return (
        <Section title="Payments & customer account" icon={CreditCard} testid="lead-payments-panel">
            {error ? (
                <p className="text-sm" style={{ color: "var(--color-danger)" }} data-testid="payments-error">
                    <AlertTriangle size={14} className="inline" /> {error}
                </p>
            ) : null}
            <div className="grid sm:grid-cols-2 gap-2 text-sm">
                <Field label="Stripe customer" value={pay.stripe_customer_id || "—"} />
                <Field label="Stripe invoice" value={inv.stripe_invoice_id || "—"} />
                <Field label="Invoice status" value={inv.status || "—"} />
                <Field label="Payment status" value={pay.status || "—"} />
                <Field label="Amount quoted" value={(lead.estimate?.amount_quoted ?? "—")} />
                <Field label="Amount paid" value={pay.amount_paid ?? "—"} />
                <Field label="Balance due" value={pay.balance_due ?? "—"} />
                <Field label="Method" value={pay.payment_method_type || "—"} />
                <Field label="Last event" value={pay.last_event_at || "—"} />
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
                <button onClick={() => run("customer-account")} disabled={!!busy}
                        className="a1-btn-secondary" data-testid="btn-create-customer-account">
                    {busy === "customer-account" ? "…" : "Create dry-run customer account"}
                </button>
                <button onClick={() => run("stripe/customer")} disabled={!!busy}
                        className="a1-btn-secondary" data-testid="btn-stripe-customer">
                    {busy === "stripe/customer" ? "…" : "Create Stripe customer"}
                </button>
                <div className="flex gap-2 col-span-full">
                    <input
                        type="number" min="1" step="1"
                        className="a1-input flex-1"
                        value={amount}
                        onChange={(e) => setAmount(Number(e.target.value))}
                        data-testid="invoice-amount"
                    />
                    <button
                        onClick={() => run("stripe/invoice", { amount_usd: amount, description: "A1 Screening — services rendered" })}
                        disabled={!!busy}
                        className="a1-btn-primary whitespace-nowrap"
                        data-testid="btn-create-invoice"
                    >
                        {busy === "stripe/invoice" ? "…" : "Create dry-run invoice"}
                    </button>
                </div>
                <button onClick={() => run("stripe/invoice/mark-sent")} disabled={!!busy || !inv.stripe_invoice_id}
                        className="a1-btn-secondary" data-testid="btn-mark-invoice-sent">
                    <Send size={14} /> {busy === "stripe/invoice/mark-sent" ? "…" : "Mark invoice sent"}
                </button>
                <button onClick={() => run("stripe/payment-received")} disabled={!!busy || !inv.stripe_invoice_id}
                        className="a1-btn-secondary" data-testid="btn-payment-received">
                    {busy === "stripe/payment-received" ? "…" : "Mark payment received"}
                </button>
            </div>
        </Section>
    );
}

// ---------- Stripe payment link (checkout) ----------
export function PaymentLinkCard({ lead, onChange }) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [copied, setCopied] = useState(false);
    const inv = lead.invoice || {};
    const paid = inv.status === "paid" || lead.payment?.status === "paid";
    const token = lead.payment_link?.token;
    const url = token ? `${window.location.origin}/pay/${token}` : null;

    const create = async () => {
        setBusy(true); setError("");
        try {
            await api.post(`/admin/leads/${lead.id}/payment-link`);
            onChange?.();
        } catch (e) {
            setError(e?.response?.data?.detail || "Couldn't create the link.");
        } finally { setBusy(false); }
    };

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch { /* clipboard unavailable */ }
    };

    return (
        <Section title="Online payment link" icon={CreditCard} testid="lead-payment-link-panel">
            {paid ? (
                <p className="text-sm m-0" style={{ color: "var(--color-accent)" }} data-testid="payment-link-paid">
                    ✓ This invoice is paid — no link needed.
                </p>
            ) : url ? (
                <>
                    <div className="flex gap-2 items-center">
                        <input readOnly value={url} className="a1-input flex-1 text-xs" data-testid="payment-link-url" onFocus={(e) => e.target.select()} />
                        <button type="button" onClick={copy} className="a1-btn-primary whitespace-nowrap" data-testid="payment-link-copy">
                            <Copy size={14} /> {copied ? "Copied!" : "Copy"}
                        </button>
                    </div>
                    <p className="a1-helper">
                        Text or email this link to the customer — it opens a page with the invoice amount and a secure Stripe checkout. The lead flips to Paid automatically when the payment goes through.
                    </p>
                </>
            ) : (
                <>
                    {error ? <p className="text-sm m-0" style={{ color: "var(--color-danger)" }} data-testid="payment-link-error">{error}</p> : null}
                    <button
                        type="button"
                        onClick={create}
                        disabled={busy || !inv.amount}
                        className="a1-btn-primary"
                        data-testid="btn-create-payment-link"
                    >
                        {busy ? "…" : "Create payment link"}
                    </button>
                    <p className="a1-helper">
                        {inv.amount
                            ? `Creates a shareable link to collect the $${inv.amount} invoice by card via Stripe.`
                            : "Create an invoice with an amount first, then generate the payment link."}
                    </p>
                </>
            )}
        </Section>
    );
}

// ---------- GHL ----------
export function GHLPanel({ lead, onChange }) {
    const [busy, setBusy] = useState(false);
    const ghl = lead.ghl || {};
    const sync = async () => {
        setBusy(true);
        try {
            await api.post(`/admin/leads/${lead.id}/ghl/sync`);
            onChange?.();
        } finally { setBusy(false); }
    };
    return (
        <Section title="GoHighLevel CRM" icon={RefreshCw} testid="lead-ghl-panel">
            <div className="grid sm:grid-cols-2 gap-2 text-sm">
                <Field label="Contact ID" value={ghl.contact_id || "—"} />
                <Field label="Opportunity ID" value={ghl.opportunity_id || "—"} />
                <Field label="Last sync" value={ghl.last_sync_at || "—"} />
                <Field label="Last status" value={ghl.last_sync_status || "—"} />
            </div>
            <button onClick={sync} disabled={busy} className="a1-btn-secondary" data-testid="btn-ghl-sync">
                <RefreshCw size={14} /> {busy ? "Syncing…" : "Retry GHL sync (dry-run)"}
            </button>
            <p className="a1-helper">
                Dry-run logs the intended payload to the automation event log without contacting GoHighLevel.
            </p>
        </Section>
    );
}

// ---------- Make.com ----------
export function MakePanel({ lead }) {
    const [busy, setBusy] = useState(null);
    const events = [
        { key: "new-lead", label: "New lead" },
        { key: "estimate-sent", label: "Estimate sent" },
        { key: "payment-received", label: "Payment received" },
        { key: "job-completed", label: "Job completed" },
        { key: "review-request", label: "Review request" },
    ];
    const fire = async (e) => {
        setBusy(e);
        try {
            await api.post(`/admin/leads/${lead.id}/make/${e}`);
        } finally { setBusy(null); }
    };
    return (
        <Section title="Make.com webhooks" icon={Webhook} testid="lead-make-panel">
            <div className="grid sm:grid-cols-2 gap-2">
                {events.map((e) => (
                    <button key={e.key}
                            onClick={() => fire(e.key)}
                            disabled={busy === e.key}
                            className="a1-btn-secondary"
                            data-testid={`btn-make-${e.key}`}>
                        {busy === e.key ? "…" : e.label}
                    </button>
                ))}
            </div>
            <p className="a1-helper">
                Dry-run logs the full payload to the automation event log instead of POSTing to Make.com. Set <code>MAKE_*_WEBHOOK_URL</code> env vars to enable live mode after launch.
            </p>
        </Section>
    );
}

// ---------- Customer portal preview link ----------
export function PortalPanel({ lead, onChange }) {
    const [busy, setBusy] = useState(false);
    const [link, setLink] = useState(null);
    const [copied, setCopied] = useState(false);
    const generate = async () => {
        setBusy(true);
        try {
            const r = await api.post(`/admin/leads/${lead.id}/portal-link`);
            setLink(r.data.link);
            onChange?.();
        } finally { setBusy(false); }
    };
    const copy = async () => {
        if (!link?.url) return;
        try { await navigator.clipboard.writeText(link.url); setCopied(true); setTimeout(() => setCopied(false), 1500); }
        catch { /* ignore */ }
    };
    return (
        <Section title="Customer portal preview" icon={ExternalLink} testid="lead-portal-panel">
            <button onClick={generate} disabled={busy} className="a1-btn-primary" data-testid="btn-portal-link">
                {busy ? "…" : "Generate dry-run customer portal link"}
            </button>
            {link ? (
                <div className="space-y-2">
                    <p className="text-xs uppercase tracking-[0.18em] font-bold" style={{ color: "var(--color-accent)" }}>
                        {link.preview_label || link.mode}
                    </p>
                    <div className="flex gap-2">
                        <input
                            readOnly
                            value={link.url}
                            className="a1-input flex-1 font-mono text-xs"
                            data-testid="portal-link-url"
                        />
                        <button onClick={copy} className="a1-btn-secondary whitespace-nowrap" data-testid="btn-portal-copy">
                            <Copy size={14} /> {copied ? "Copied" : "Copy"}
                        </button>
                        <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="a1-btn-secondary whitespace-nowrap"
                            data-testid="btn-portal-open"
                        >
                            <ExternalLink size={14} /> Open
                        </a>
                    </div>
                    <p className="a1-helper">
                        Single-use, expires {new Date(link.expires_at).toLocaleString()}. Magic link is admin-generated only in dry-run mode — not emailed to the customer.
                    </p>
                </div>
            ) : null}
        </Section>
    );
}

function Field({ label, value }) {
    return (
        <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: "var(--color-text-dim)" }}>{label}</p>
            <p className="text-sm font-semibold" style={{ color: "var(--color-text)" }}>{String(value)}</p>
        </div>
    );
}
